import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  DocumentIcon,
  DocumentTextIcon,
  PaperClipIcon,
  PhotoIcon,
  SparklesIcon,
  SpeakerWaveIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolidIcon,
  MinusCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/solid';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { AudioConversationPreview } from '@/components/audio-conversation-preview';
import { ConversationViewer } from '@/components/conversation-viewer';
import { BlobPreview } from '@/components/documents-v2/components/inspector/BlobPreview';
import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { Heading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useGetArtifactUnpackedQuery } from '@/services/api/artifactService';
import {
  type DeliverableMatchSchema,
  type DeliverableMatchStatus,
  type DeliverableSchemaRead,
  type DeliverableStatus,
  type DetailedArtifactSchema,
  enhancedApi as deliverablesApi,
  type TaskGroups,
} from '@/services/api/generated/deliverablesServicev2';
import { ArtifactType } from '@/services/types/artifact-digest-types';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const GROUP_ORDER: TaskGroups[] = [
  'Intake',
  'Police Report & Investigation',
  'Medical Records',
  'Treatment',
  '1P Insurance',
  '3P Insurance',
  'Demand Letter',
  'Liens',
  'Additional Insurance',
  'Miscellaneous',
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const getArtifactIcon = (
  a?: DetailedArtifactSchema | null,
): React.ElementType => {
  if (!a) return DocumentIcon;
  if (a.artifact_type === ArtifactType.AUDIO_CONVERSATION)
    return SpeakerWaveIcon;
  if (a.artifact_type === ArtifactType.BLOB) {
    if (a.blob?.file_mime_type?.startsWith('image/')) return PhotoIcon;
    if (a.blob?.file_mime_type === 'application/pdf') return DocumentTextIcon;
    return PaperClipIcon;
  }
  return DocumentTextIcon;
};

const getArtifactTitle = (a?: DetailedArtifactSchema | null): string => {
  if (!a) return 'Unknown Document';
  return (
    a.artifact_digest?.title ||
    a.blob?.original_filename ||
    a.blob?.name ||
    a.message?.subject ||
    a.name ||
    'Untitled'
  );
};

const getEntityLabel = (d: DeliverableSchemaRead): string | null => {
  const { task } = d;
  if (task.provider) return task.provider.name;
  if (task.coverage?.carrier?.name) return task.coverage.carrier.name;
  if (task.police_report?.police_department?.name)
    return task.police_report.police_department.name;
  return null;
};

const groupByEntity = (
  deliverables: DeliverableSchemaRead[],
): Map<string | null, DeliverableSchemaRead[]> => {
  const groups = new Map<string | null, DeliverableSchemaRead[]>();
  deliverables.forEach((d) => {
    const entity = getEntityLabel(d);
    const existing = groups.get(entity) ?? [];
    groups.set(entity, [...existing, d]);
  });
  return groups;
};

const getMatchStatusColor = (
  isApproved: boolean,
  isRejected: boolean,
): string => {
  if (isApproved) return 'bg-emerald-100 text-emerald-600';
  if (isRejected) return 'bg-red-100 text-red-600';
  return 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400';
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// --- Artifact Preview ---

const ArtifactPreview: React.FC<{ artifactId: string; matterId: string }> = ({
  artifactId,
  matterId,
}) => {
  const { data: artifact, isLoading } = useGetArtifactUnpackedQuery(
    { matterId, artifactId },
    { skip: !artifactId || !matterId },
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-50/50">
        <div className="flex flex-col items-center gap-2">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      </div>
    );
  }

  if (!artifact) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-zinc-400 bg-zinc-50/50">
        <DocumentTextIcon className="w-10 h-10 mb-3 opacity-20" />
        <p className="text-sm font-medium">No content available</p>
      </div>
    );
  }

  if (artifact.artifact_type === ArtifactType.BLOB && artifact.blob) {
    return <BlobPreview file={artifact.blob} />;
  }
  if (
    artifact.artifact_type === ArtifactType.AUDIO_CONVERSATION ||
    artifact.artifact_type === ArtifactType.TRANSCRIPT
  ) {
    return <AudioConversationPreview artifact={artifact} />;
  }
  if (artifact.artifact_type === ArtifactType.CONVERSATION) {
    return <ConversationViewer artifact={artifact} />;
  }

  return (
    <div className="flex items-center justify-center h-full text-zinc-500 text-sm bg-zinc-50/50">
      Preview format not supported
    </div>
  );
};

// --- Match Item ---

const MatchItem: React.FC<{
  match: DeliverableMatchSchema;
  isSelected: boolean;
  onSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
}> = ({ match, isSelected, onSelect, onApprove, onReject }) => {
  const Icon = getArtifactIcon(match.artifact);
  const title = getArtifactTitle(match.artifact);
  const isApproved = match.status === 'Approved';
  const isRejected = match.status === 'Rejected';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'group flex items-center gap-3 p-1.5 rounded-md border text-sm transition-all cursor-pointer mb-1.5 last:mb-0 text-left',
        isSelected
          ? 'border-indigo-500 bg-indigo-50/50 dark:border-indigo-500/50 dark:bg-indigo-900/20'
          : 'border-zinc-200 bg-white hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50 dark:hover:bg-zinc-800',
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-md flex-shrink-0',
          getMatchStatusColor(isApproved, isRejected),
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'truncate font-medium text-zinc-900 dark:text-zinc-100',
              isRejected && 'line-through opacity-70',
            )}
            title={title}
          >
            {title}
          </span>
          {match.status === 'Needs Review' && (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity focus-within:opacity-100">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                plain
                className={cn(
                  '!p-1',
                  isApproved
                    ? 'text-emerald-600'
                    : 'text-zinc-400 hover:text-emerald-600',
                )}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onApprove();
                }}
              >
                <CheckCircleIcon
                  className={cn('h-5 w-5', isApproved && 'fill-current')}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Approve Match</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                plain
                className={cn(
                  '!p-1',
                  isRejected
                    ? 'text-red-600'
                    : 'text-zinc-400 hover:text-red-600',
                )}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onReject();
                }}
              >
                <XCircleIcon
                  className={cn('h-5 w-5', isRejected && 'fill-current')}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reject Match</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

// --- Deliverable Row ---

const DeliverableRow: React.FC<{
  deliverable: DeliverableSchemaRead;
  matches: DeliverableMatchSchema[];
  selectedMatchId: string | null;
  isSelected: boolean;
  onSelect: () => void;
  onSelectMatch: (id: string) => void;
  onUpdateStatus: (s: DeliverableStatus) => void;
  onApproveMatch: (id: string) => void;
  onRejectMatch: (id: string) => void;
}> = ({
  deliverable,
  matches,
  selectedMatchId,
  isSelected,
  onSelect,
  onSelectMatch,
  onUpdateStatus,
  onApproveMatch,
  onRejectMatch,
}) => {
  const isDone = deliverable.status === 'Satisfied';
  const isBypassed = deliverable.status === 'Bypassed';
  const hasPendingMatches =
    deliverable.pending_matches > 0 && !isDone && !isBypassed;
  const totalMatches = matches.length;

  let StatusIcon = QuestionMarkCircleIcon;
  let statusColor = 'text-zinc-300 dark:text-zinc-600';
  let textStyle = 'text-zinc-900 dark:text-zinc-100';

  if (isDone) {
    StatusIcon = CheckCircleSolidIcon;
    statusColor = 'text-emerald-500';
    textStyle =
      'text-zinc-500 dark:text-zinc-500 line-through decoration-zinc-500/30';
  } else if (isBypassed) {
    StatusIcon = MinusCircleIcon;
    statusColor = 'text-zinc-300 dark:text-zinc-600';
    textStyle =
      'text-zinc-400 dark:text-zinc-600 line-through decoration-zinc-400/30';
  } else if (hasPendingMatches) {
    StatusIcon = SparklesIcon;
    statusColor = 'text-amber-500';
  }

  const handleStatusAction = (s: DeliverableStatus) => {
    onUpdateStatus(s);
  };

  let badgeContent = null;
  if (!isDone && !isBypassed && hasPendingMatches) {
    badgeContent = (
      <Badge color="amber" className="mr-2">
        {deliverable.pending_matches} new
      </Badge>
    );
  } else if (totalMatches > 0) {
    badgeContent = (
      <Badge color="zinc" className="mr-2 opacity-70">
        {totalMatches} match{totalMatches !== 1 ? 'es' : ''}
      </Badge>
    );
  }

  return (
    <div
      className={cn(
        'group flex flex-col transition-all duration-200 border-b border-zinc-100 dark:border-zinc-800/50 relative',
        isSelected
          ? 'bg-zinc-50 dark:bg-zinc-900'
          : 'bg-white dark:bg-zinc-950 hover:bg-zinc-50/50 dark:hover:bg-zinc-900/30',
      )}
    >
      {isSelected && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 z-20" />
      )}
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'flex items-center gap-3 p-2.5 cursor-pointer min-h-[40px] text-left outline-none focus:bg-zinc-50 dark:focus:bg-zinc-900',
          isSelected && 'pl-4', // Offset slightly for visual balance with border
        )}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {/* Chevron for Collapsible State */}
        <ChevronRightIcon
          className={cn(
            'h-3 w-3 text-zinc-400 transition-transform flex-shrink-0',
            isSelected && 'rotate-90',
          )}
        />

        {/* Status Dropdown (Unified Indicator) */}
        <div
          className="flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          role="button"
          tabIndex={-1}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center justify-center rounded-md p-1 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 outline-none focus:ring-2 focus:ring-indigo-500',
                  statusColor,
                )}
              >
                <StatusIcon className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => handleStatusAction('Satisfied')}>
                <CheckCircleSolidIcon className="mr-2 h-4 w-4 text-emerald-500" />
                <span>Satisfied</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusAction('Bypassed')}>
                <MinusCircleIcon className="mr-2 h-4 w-4 text-zinc-400" />
                <span>Not Applicable</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusAction('Pending')}>
                <QuestionMarkCircleIcon className="mr-2 h-4 w-4 text-amber-500" />
                <span>Pending</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <div
            className={cn('text-sm font-medium transition-colors', textStyle)}
          >
            {deliverable.name}
          </div>
        </div>

        {/* Badges / Meta */}
        {badgeContent}
      </div>

      {/* Expanded Content */}
      {isSelected && (
        <div className="px-3 pb-3 pt-0 pl-11">
          {/* Definition / Prompt */}
          <div className="mb-3 text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
            {deliverable.prompt}
            {deliverable.task_id && (
              <div className="mt-1">
                <Link
                  to={`/matters/${deliverable.task.matter.id}/tasks/${deliverable.task_id}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 font-medium text-indigo-500 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  View Task <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>

          {/* Matches List */}
          {matches.length > 0 ? (
            <div className="space-y-1 mt-2">
              <div className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider mb-2">
                Potential Matches
              </div>
              {matches.map((match) => (
                <MatchItem
                  key={match.id}
                  match={match}
                  isSelected={match.id === selectedMatchId}
                  onSelect={() => onSelectMatch(match.id)}
                  onApprove={() => onApproveMatch(match.id)}
                  onReject={() => onRejectMatch(match.id)}
                />
              ))}
            </div>
          ) : (
            <div className="mt-2 p-2 rounded-md bg-zinc-50 dark:bg-zinc-800/30 border border-dashed border-zinc-200 dark:border-zinc-700/50 text-center">
              <Text className="text-xs italic">No matches found yet.</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Group Section ---

const TaskGroupSection: React.FC<{
  groupName: string;
  deliverables: DeliverableSchemaRead[];
  isExpanded: boolean;
  onToggle: () => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  matchesMap: Record<string, DeliverableMatchSchema[]>;
  selectedMatchId: string | null;
  onSelectMatch: (id: string) => void;
  onUpdateStatus: (d: DeliverableSchemaRead, s: DeliverableStatus) => void;
  onApproveMatch: (match: DeliverableMatchSchema) => void;
  onRejectMatch: (match: DeliverableMatchSchema) => void;
}> = ({
  groupName,
  deliverables,
  isExpanded,
  onToggle,
  selectedId,
  onSelect,
  matchesMap,
  selectedMatchId,
  onSelectMatch,
  onUpdateStatus,
  onApproveMatch,
  onRejectMatch,
}) => {
  const entityGroups = React.useMemo(
    () => groupByEntity(deliverables),
    [deliverables],
  );

  const satisfiedCount = deliverables.filter(
    (d) => d.status === 'Satisfied',
  ).length;
  const totalCount = deliverables.length;

  // Track collapsed subgroups (default expanded)
  const [collapsedSubgroups, setCollapsedSubgroups] = React.useState<
    Set<string | null>
  >(new Set());

  const toggleSubgroup = (entity: string | null) => {
    setCollapsedSubgroups((prev) => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);
      return next;
    });
  };

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300 transition-colors bg-white dark:bg-zinc-950 sticky top-0 z-10 border-b border-zinc-100 dark:border-zinc-800"
      >
        <ChevronRightIcon
          className={cn(
            'h-3 w-3 transition-transform text-zinc-400',
            isExpanded && 'rotate-90',
          )}
        />
        {groupName}
        <Badge
          color={satisfiedCount === totalCount ? 'emerald' : 'zinc'}
          className="ml-auto px-1.5 py-0 text-[10px]"
        >
          {satisfiedCount}/{totalCount}
        </Badge>
      </button>

      {isExpanded && (
        <div className="pb-2">
          {Array.from(entityGroups.entries()).map(([entity, items]) => {
            const isSubgroupCollapsed = collapsedSubgroups.has(entity);
            return (
              <div key={entity ?? 'default'}>
                {entity && (
                  <button
                    type="button"
                    onClick={() => toggleSubgroup(entity)}
                    className="flex w-full items-center gap-2 pl-9 pr-3 py-2 text-xs font-bold text-zinc-600 dark:text-zinc-400 uppercase tracking-wider bg-zinc-50 dark:bg-zinc-900/50 border-y border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer text-left group/subgroup"
                    title={entity}
                  >
                    <ChevronRightIcon
                      className={cn(
                        'h-3.5 w-3.5 transition-transform flex-shrink-0 text-zinc-400 group-hover/subgroup:text-zinc-600',
                        !isSubgroupCollapsed && 'rotate-90',
                      )}
                    />
                    <span className="truncate min-w-0 flex-1">{entity}</span>
                  </button>
                )}
                {(!entity || !isSubgroupCollapsed) &&
                  items.map((d) => (
                    <DeliverableRow
                      key={d.id}
                      deliverable={d}
                      matches={matchesMap[d.id] || []}
                      selectedMatchId={selectedMatchId}
                      isSelected={d.id === selectedId}
                      onSelect={() =>
                        onSelect(selectedId === d.id ? null : d.id)
                      }
                      onSelectMatch={onSelectMatch}
                      onUpdateStatus={(s) => onUpdateStatus(d, s)}
                      onApproveMatch={(matchId) => {
                        const match = matchesMap[d.id]?.find(
                          (m) => m.id === matchId,
                        );
                        if (match) onApproveMatch(match);
                      }}
                      onRejectMatch={(matchId) => {
                        const match = matchesMap[d.id]?.find(
                          (m) => m.id === matchId,
                        );
                        if (match) onRejectMatch(match);
                      }}
                    />
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

interface DeliverablesPageProps {
  matterId: string;
}

export const DeliverablesPage: React.FC<DeliverablesPageProps> = ({
  matterId,
}) => {
  const [selectedDeliverableId, setSelectedDeliverableId] = React.useState<
    string | null
  >(null);
  const [selectedMatchId, setSelectedMatchId] = React.useState<string | null>(
    null,
  );
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    new Set(GROUP_ORDER),
  );

  const { data: deliverables, isLoading } =
    deliverablesApi.useMatterDeliverablesListQuery(
      { matterId: matterId ?? '' },
      { skip: !matterId, pollingInterval: 30000 },
    );

  const [updateDeliverable] =
    deliverablesApi.useTaskDeliverablesUpdateMutation();
  const [bypassDeliverable] =
    deliverablesApi.useTaskDeliverablesBypassMutation();
  const [approveMatch] = deliverablesApi.useDeliverableMatchesApproveMutation();
  const [rejectMatch] = deliverablesApi.useDeliverableMatchesRejectMutation();
  const [resetMatch] =
    deliverablesApi.useDeliverableMatchesPartialUpdateMutation();
  const [scanDeliverables, { isLoading: isScanningDeliverables }] =
    deliverablesApi.useMatterDeliverablesScanMutation();

  // Logic: Pre-process matches for each deliverable to avoid re-sorting on every render in the row
  const deliverablesWithSortedMatches = React.useMemo(() => {
    if (!deliverables) return new Map<string, DeliverableMatchSchema[]>();
    const map = new Map<string, DeliverableMatchSchema[]>();
    deliverables.forEach((d) => {
      if (!d.matches) {
        map.set(d.id, []);
        return;
      }
      const sorted = [...d.matches].sort((a, b) => {
        // Needs Review (3) > Approved (2) > Rejected (1)
        const score = (s?: string | null) => {
          if (!s || s === 'Needs Review') return 3;
          if (s === 'Approved') return 2;
          return 1;
        };
        const statusDiff = score(b.status) - score(a.status);
        if (statusDiff !== 0) return statusDiff;
        return (b.confidence ?? 0) - (a.confidence ?? 0);
      });
      map.set(d.id, sorted);
    });
    return map;
  }, [deliverables]);

  // Grouping logic
  const grouped = React.useMemo(() => {
    if (!deliverables) return {};
    const map: Record<string, DeliverableSchemaRead[]> = {};
    deliverables.forEach((d) => {
      const g = d.task_canonical_group || 'Miscellaneous';
      if (!map[g]) map[g] = [];
      map[g].push(d);
    });
    return map;
  }, [deliverables]);

  const sortedGroups = React.useMemo(
    () => GROUP_ORDER.filter((g) => grouped[g]),
    [grouped],
  );

  // Selection Logic
  const selectedDeliverable = React.useMemo(
    () => deliverables?.find((d) => d.id === selectedDeliverableId) ?? null,
    [deliverables, selectedDeliverableId],
  );

  const selectedMatch = React.useMemo(() => {
    if (!selectedDeliverableId || !selectedMatchId) return null;
    const matches = deliverablesWithSortedMatches.get(selectedDeliverableId);
    return matches?.find((m) => m.id === selectedMatchId) ?? null;
  }, [selectedDeliverableId, selectedMatchId, deliverablesWithSortedMatches]);

  // Auto-selection effects
  const hasInitialSelection = React.useRef(false);

  // 1. If nothing selected, select first pending with matches, or just first (only once)
  React.useEffect(() => {
    if (!hasInitialSelection.current && deliverables?.length) {
      const needsReview = deliverables.find(
        (d) => d.pending_matches > 0 && d.status !== 'Satisfied',
      );
      setSelectedDeliverableId(needsReview?.id ?? deliverables[0].id);
      hasInitialSelection.current = true;
    }
  }, [deliverables]);

  // 2. When deliverable changes, select first match if none selected
  React.useEffect(() => {
    if (selectedDeliverableId) {
      const matches =
        deliverablesWithSortedMatches.get(selectedDeliverableId) || [];
      // If current selected match is NOT in this deliverable, reset/select new
      const isMatchInDeliverable =
        selectedMatchId && matches.find((m) => m.id === selectedMatchId);

      if (!isMatchInDeliverable && matches.length > 0) {
        // Select first 'Needs Review' or just first
        const pending = matches.find(
          (m) => !m.status || m.status === 'Needs Review',
        );
        setSelectedMatchId(pending?.id ?? matches[0].id);
      } else if (matches.length === 0) {
        setSelectedMatchId(null);
      }
    }
  }, [selectedDeliverableId, selectedMatchId, deliverablesWithSortedMatches]);

  // Handlers
  const handleDeliverableStatus = async (
    d: DeliverableSchemaRead,
    status: DeliverableStatus,
  ) => {
    if (!matterId) return;
    try {
      if (status === 'Bypassed') {
        await bypassDeliverable({
          matterId,
          taskId: d.task_id,
          id: d.id,
        }).unwrap();
      } else {
        await updateDeliverable({
          matterId,
          taskId: d.task_id,
          id: d.id,
          updateDeliverableStatus: { status },
        }).unwrap();
      }
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleMatchStatus = async (
    m: DeliverableMatchSchema,
    status: DeliverableMatchStatus,
  ) => {
    // Need to find which deliverable this match belongs to if not using selectedDeliverable
    // But we iterate matches from deliverables, so we can pass the deliverable or use the map
    // We'll rely on the passed match object having access to its parent ID if needed,
    // OR just pass the required IDs from the parent component.
    // The `DeliverableMatchSchema` has `deliverable_id`.
    if (!matterId) return;

    // Use deliverable_id from match
    const deliverableId = m.deliverable_id;
    // We need task_id. Find deliverable in list.
    const deliverable = deliverables?.find((d) => d.id === deliverableId);
    if (!deliverable) return;

    try {
      const params = {
        matterId,
        taskId: deliverable.task_id,
        deliverableId: deliverable.id,
        id: m.id,
      };

      if (m.status === status && status !== 'Needs Review') {
        await resetMatch({
          ...params,
          patchedUpdateDeliverableMatch: { id: m.id, status: 'Needs Review' },
        }).unwrap();
        toast.info('Match status reset');
        return;
      }

      if (status === 'Approved') {
        await approveMatch(params).unwrap();
        toast.success('Match approved');
      } else if (status === 'Rejected') {
        await rejectMatch(params).unwrap();
        toast.success('Match rejected');
      }
    } catch {
      toast.error('Failed to update match');
    }
  };

  const toggleGroup = (g: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  const handleScanDeliverables = async () => {
    if (!matterId) return;
    try {
      await scanDeliverables({ matterId }).unwrap();
      toast.success('Deliverable scan started');
    } catch {
      toast.error('Failed to start deliverable scan');
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <ArrowPathIcon className="w-6 h-6 text-zinc-300 animate-spin" />
      </div>
    );
  }

  // Preview Content Logic
  let previewContent;
  if (selectedMatch) {
    previewContent = (
      <div className="flex flex-col h-full">
        {selectedMatch.context?.rationale && (
          <div className="flex-shrink-0 p-4 bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-start gap-3">
              <SparklesIcon className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  Match Rationale
                </p>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                  {selectedMatch.context.rationale}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 min-h-0 relative">
          <ArtifactPreview
            artifactId={selectedMatch.artifact?.id || ''}
            matterId={matterId || ''}
          />
        </div>
      </div>
    );
  } else if (selectedDeliverable) {
    // If no match selected, but deliverable selected
    // Show "No matches" placeholder or specific instructions
    previewContent = (
      <div className="flex flex-col items-center justify-center h-full text-zinc-400">
        <DocumentIcon className="w-12 h-12 mb-3 opacity-20" />
        <p className="text-sm font-medium text-zinc-500">
          {selectedDeliverable.matches && selectedDeliverable.matches.length > 0
            ? 'Select a match to preview'
            : 'No matches found for this deliverable'}
        </p>
      </div>
    );
  } else {
    previewContent = (
      <div className="flex flex-col items-center justify-center h-full text-zinc-400">
        <Text>Select a deliverable to begin</Text>
      </div>
    );
  }

  return (
    <div className="h-full flex bg-zinc-50 dark:bg-zinc-950 overflow-hidden">
      {/* COLUMN 1: Audit Checklist (Fixed Width ~450px) */}
      <div className="w-[480px] flex-shrink-0 flex flex-col border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 z-10 shadow-lg shadow-zinc-900/5">
        <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between bg-white dark:bg-zinc-950 sticky top-0 z-20">
          <Heading level={3}>Document Audit</Heading>
          <Button
            onClick={handleScanDeliverables}
            disabled={isScanningDeliverables}
            outline
            className="h-8 px-2.5 text-xs"
          >
            <ArrowPathIcon
              className={cn(
                'w-3.5 h-3.5 mr-1.5',
                isScanningDeliverables && 'animate-spin',
              )}
            />
            Scan Case File
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {sortedGroups.map((groupName) => (
            <TaskGroupSection
              key={groupName}
              groupName={groupName}
              deliverables={grouped[groupName]}
              isExpanded={expandedGroups.has(groupName)}
              onToggle={() => toggleGroup(groupName)}
              selectedId={selectedDeliverableId}
              onSelect={setSelectedDeliverableId}
              matchesMap={Object.fromEntries(deliverablesWithSortedMatches)}
              selectedMatchId={selectedMatchId}
              onSelectMatch={setSelectedMatchId}
              onUpdateStatus={handleDeliverableStatus}
              onApproveMatch={(m) => handleMatchStatus(m, 'Approved')}
              onRejectMatch={(m) => handleMatchStatus(m, 'Rejected')}
            />
          ))}
          <div className="h-12" /> {/* Spacer */}
        </div>
      </div>

      {/* COLUMN 2: Preview Area (Flex Grow) */}
      <div className="flex-1 flex flex-col min-w-0 bg-zinc-100 dark:bg-zinc-900 relative">
        {previewContent}
      </div>
    </div>
  );
};
